import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

const DOW_LABELS = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};
const ALL_DOWS = [1, 2, 3, 4, 5, 6, 7];

// Three availability modes per day, mirroring the
// request-availability modal's radio group. Time pickers only
// render when mode === "custom"; the other two modes write fixed
// 0/0 (unavailable) or 0/24 (all day) windows server-side.
const MODE_CUSTOM = "custom";
const MODE_ALL_DAY = "all_day";
const MODE_UNAVAILABLE = "unavailable";
const MODE_OPTIONS = [
  { value: MODE_CUSTOM, label: "Custom hours" },
  { value: MODE_ALL_DAY, label: "All day" },
  { value: MODE_UNAVAILABLE, label: "Unavailable" },
];

function inferMode(availableFrom, availableTo, isOff) {
  // Detect the canonical 0/0 (off) and 0/24 (all-day) windows so the
  // dropdown initializes to the right preset when the LLM proposed
  // one or when the row reloads from server state.
  if (isOff) return MODE_UNAVAILABLE;
  if (availableFrom === 0 && availableTo === 0) return MODE_UNAVAILABLE;
  if (availableFrom === 0 && availableTo === 24) return MODE_ALL_DAY;
  return MODE_CUSTOM;
}

// Format a row from the proposal's ``current_availability`` snapshot
// for display alongside un-included day rows. The snapshot is keyed
// by string dow ("1".."7") and each value has available_from /
// available_to. Missing dow → no current row → render "No
// availability set" so the manager knows what they'd be setting
// from blank.
function fmtCurrent(current) {
  // The italic gray styling on the rendered text already signals
  // "this is the current state" — prefixing every line with
  // "Currently" was redundant.
  if (!current) return "No availability set";
  const af = current.available_from;
  const at = current.available_to;
  if (af === 0 && at === 0) return "Unavailable";
  if (af === 0 && at === 24) return "All day";
  return `${fmtQhnum(af)} – ${fmtQhnum(at)}`;
}

// Quarter-hour increments for the time pickers (matches the rest of
// the scheduling UI's granularity).
const QHNUM_OPTIONS = (() => {
  const out = [];
  for (let q = 0; q < 24 * 4; q++) {
    out.push(q / 4);
  }
  return out;
})();

function fmtQhnum(qhnum) {
  if (qhnum === null || qhnum === undefined) return "";
  const hour = Math.floor(qhnum) % 24;
  const minute = Math.round((qhnum - Math.floor(qhnum)) * 60);
  const period = hour < 12 ? "AM" : "PM";
  let displayHour = hour > 12 ? hour - 12 : hour;
  if (displayHour === 0) displayHour = 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
}

function todayISO() {
  // Date input expects YYYY-MM-DD in local time. Pad month/day so
  // 2026-05-04 doesn't accidentally render as 2026-5-4.
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function AvailabilityProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const summary = proposal.summary || {};

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  // The LLM may only have specified some days. Build a map keyed by
  // dow so we can render all 7 rows; days not in the proposal start
  // empty (manager can opt them in by toggling Off or picking times).
  // Days that ARE in the proposal start enabled with the proposed
  // times.
  const initialResolved = Array.isArray(summary.resolved_days)
    ? summary.resolved_days
    : [];
  const initialDayMap = {};
  for (const d of initialResolved) {
    if (d && d.dow >= 1 && d.dow <= 7) {
      const af =
        d.available_from === null || d.available_from === undefined
          ? 9.0
          : d.available_from;
      const at =
        d.available_to === null || d.available_to === undefined
          ? 17.0
          : d.available_to;
      const mode = inferMode(af, at, !!d.is_off);
      initialDayMap[d.dow] = {
        included: true,
        mode,
        // For preset modes, store the canonical 9–5 fallback so
        // switching back to Custom doesn't show 0/0 or 0/24.
        available_from: mode === MODE_CUSTOM ? af : 9.0,
        available_to: mode === MODE_CUSTOM ? at : 17.0,
      };
    }
  }

  // Build state for all 7 days. Days not in the proposal start with
  // included=false so the manager can leave them untouched (the
  // backend's helper only writes the days[] array we send, so an
  // unincluded day keeps its existing availability).
  const [dayState, setDayState] = useState(() => {
    const currentByDow = summary.current_availability || {};
    const out = {};
    for (const dow of ALL_DOWS) {
      // 1) LLM proposed this day → use its values (manager is
      //    editing what the LLM picked).
      if (initialDayMap[dow]) {
        out[dow] = initialDayMap[dow];
        continue;
      }
      // 2) LLM didn't propose this day, but employee already has
      //    current availability → seed the dropdown with that mode
      //    so checking the box shows "what's there now" rather than
      //    a blanket "All day" override of an existing setting.
      const current = currentByDow[String(dow)];
      if (current) {
        const af = current.available_from;
        const at = current.available_to;
        const mode = inferMode(af, at, false);
        out[dow] = {
          included: false,
          mode,
          // For Custom mode, pre-fill the time pickers with the
          // current times so if the manager checks the box and
          // wants to tweak hours, they see "what's there now"
          // ready to edit. Preset modes keep 9–5 as the fallback
          // for if the manager later switches to Custom.
          available_from: mode === MODE_CUSTOM ? af : 9.0,
          available_to: mode === MODE_CUSTOM ? at : 17.0,
        };
        continue;
      }
      // 3) No LLM proposal AND no current availability — brand-new
      //    setup. Default to "All day" since it's the safer choice
      //    when the manager hasn't given any hours-specific intent.
      out[dow] = {
        included: false,
        mode: MODE_ALL_DAY,
        available_from: 9.0,
        available_to: 17.0,
      };
    }
    return out;
  });

  const [activatedAt, setActivatedAt] = useState(
    summary.activated_at || todayISO()
  );

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const updateDay = (dow, patch) => {
    setDayState((prev) => {
      const next = { ...prev[dow], ...patch };

      // When switching INTO Custom mode, ensure the times are a
      // sensible window. The from/to fields in state are hidden
      // while in preset modes but kept at 9–5 so this transition
      // doesn't surprise the user with 0/0 or 0/24 leaking through.
      if (patch.mode === MODE_CUSTOM && prev[dow].mode !== MODE_CUSTOM) {
        if (
          next.available_from >= next.available_to ||
          (next.available_from === 0 && next.available_to === 0) ||
          (next.available_from === 0 && next.available_to === 24)
        ) {
          next.available_from = 9.0;
          next.available_to = 17.0;
        }
      }

      return { ...prev, [dow]: next };
    });
  };

  // ----- Validation -----
  const includedDays = ALL_DOWS.filter((dow) => dayState[dow].included);
  const anyIncludedInvalid = includedDays.some((dow) => {
    const d = dayState[dow];
    // Preset modes (All day / Unavailable) are valid by construction
    // — only Custom mode can have an invalid from/to ordering.
    if (d.mode !== MODE_CUSTOM) return false;
    return d.available_from >= d.available_to;
  });
  const confirmDisabled =
    loading ||
    includedDays.length === 0 ||
    !activatedAt ||
    anyIncludedInvalid;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };

      if (action === "confirm") {
        const days = includedDays.map((dow) => {
          const d = dayState[dow];
          if (d.mode === MODE_UNAVAILABLE) {
            return { dow, is_off: true, available_from: 0.0, available_to: 0.0 };
          }
          if (d.mode === MODE_ALL_DAY) {
            // Canonical "available all day" representation. The
            // backend's _validate_day accepts the 0–24 window
            // (passes the < check, both endpoints in [0, 24]).
            return { dow, is_off: false, available_from: 0.0, available_to: 24.0 };
          }
          return {
            dow,
            available_from: d.available_from,
            available_to: d.available_to,
            is_off: false,
          };
        });
        payload.overrides = {
          activated_at: activatedAt,
          days,
        };
      }

      const res = await postRightworkAPI(
        API_ops_assistant_confirm_action,
        payload
      );
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to set availability.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to set availability."
      );
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon
          fontSize="small"
          className="text-green-600 mt-0.5 shrink-0"
        />
        <div className="min-w-0">
          <div className="font-medium">
            Updated availability for {summary.employee_name}
          </div>
          <div className="text-xs opacity-80">
            {includedDays.length} day{includedDays.length !== 1 ? "s" : ""} ·
            starting {activatedAt}
          </div>
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Cancelled</span>
      </div>
    );
  }

  return (
    // ``max-w-md`` (448px) keeps the form readable in the expanded
    // panel state — without it the row controls stretch the full
    // panel width and become absurdly wide. The cap is generous
    // enough that the Custom-mode time pickers + "to" separator fit
    // comfortably on one second-line, and tight enough that the
    // form doesn't become a wall of stretched dropdowns.
    <div className="my-2 max-w-md rounded-lg border border-violet-200 bg-violet-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200 rounded-t-lg">
        <EventAvailableIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Set Availability
          {summary.employee_name && (
            <span className="text-violet-700 font-normal">
              {" — "}
              {summary.employee_name}
            </span>
          )}
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {/* Activation date */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Starting from
          </label>
          <input
            type="date"
            value={activatedAt}
            onChange={(e) => setActivatedAt(e.target.value)}
            className="w-full px-3 py-2 text-[13px] bg-white border border-[#bdbdbd] rounded-md hover:border-slate-900 focus:outline-none focus:border-violet-700 text-slate-900"
          />
          <div className="text-[11px] text-gray-500 mt-0.5">
            Past schedules using their previous availability are unchanged.
          </div>
        </div>

        {/* Day rows */}
        <div className="space-y-1">
          <div className="text-xs font-medium text-gray-600 mb-1">
            Days <span className="text-gray-400">(only checked days are updated)</span>
          </div>
          {ALL_DOWS.map((dow) => {
            const d = dayState[dow];
            const isCustom = d.mode === MODE_CUSTOM;
            const invalid =
              d.included && isCustom && d.available_from >= d.available_to;
            return (
              <div
                key={dow}
                className={`bg-white rounded-md border px-2 py-1 ${
                  invalid ? "border-red-300" : "border-gray-200"
                }`}
              >
                {/* Top line: include checkbox + day label + mode
                    selector (or "Not updated" placeholder). Stays
                    on one row at any panel width. */}
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 w-14 shrink-0">
                    <input
                      type="checkbox"
                      checked={d.included}
                      onChange={(e) =>
                        updateDay(dow, { included: e.target.checked })
                      }
                      className="accent-violet-700"
                    />
                    {DOW_LABELS[dow]}
                  </label>
                  {d.included ? (
                    <select
                      value={d.mode}
                      onChange={(e) => updateDay(dow, { mode: e.target.value })}
                      className="text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                      style={{ width: "auto" }}
                    >
                      {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // When the row isn't checked we're not updating
                    // this day. Show what's currently set for the
                    // employee on this dow so the manager can see
                    // the existing state before deciding whether to
                    // override it. Italic gray treatment matches
                    // the "this is read-only context" pattern.
                    <div className="text-xs italic text-gray-400 px-2 py-1">
                      {fmtCurrent(
                        (summary.current_availability || {})[String(dow)]
                      )}
                    </div>
                  )}
                </div>
                {/* Second line: time pickers, ONLY for Custom mode.
                    On a separate row so the dropdowns have full
                    panel width to render readable times — squeezing
                    them onto the same line as the mode selector
                    cuts the labels off in the docked panel. */}
                {d.included && isCustom && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <select
                      value={d.available_from}
                      onChange={(e) =>
                        updateDay(dow, { available_from: Number(e.target.value) })
                      }
                      className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    >
                      {QHNUM_OPTIONS.map((q) => (
                        <option key={`af-${dow}-${q}`} value={q}>
                          {fmtQhnum(q)}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-400 shrink-0">to</span>
                    <select
                      value={d.available_to}
                      onChange={(e) =>
                        updateDay(dow, { available_to: Number(e.target.value) })
                      }
                      className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    >
                      {QHNUM_OPTIONS.map((q) => (
                        <option key={`at-${dow}-${q}`} value={q}>
                          {fmtQhnum(q)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
          {anyIncludedInvalid && (
            <div className="text-[11px] text-red-500">
              Each Custom-mode day's start time must be before its end time
              (or pick All day / Unavailable instead).
            </div>
          )}
          {includedDays.length === 0 && (
            <div className="text-[11px] text-gray-500">
              Tick the checkbox next to a day to include it. Unchecked
              days keep their existing availability.
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white rounded-b-lg">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Set availability
        </button>
      </div>
    </div>
  );
}
